import { IErrorMessage } from '@hooks/use-error-message.ts';
import { FileTypeEnum } from '@models/media-library.model.ts';

export const mediaLibraryErrorMessages: IErrorMessage = {
  name: {
    required: 'validation_name_required',
    notOnlySpaces: 'validation_notOnly_spaces_required',
    nameExists: 'validation_name_exists',
    serverError: 'validation_server_error'
  },
  url: {
    required: 'validation_url_required',
    pattern: 'validation_url_pattern',
    notOnlySpaces: 'validation_notOnly_spaces_required'
  }
};

export const mediaLibraryDialogTabsData = [
  { label: 'tab_label_from_computer', value: 'from-computer' },
  { label: 'tab_label_from_url', value: 'from-url' }
];

export const getFileType = (data: any): FileTypeEnum => {
  const type = data.type?.toLowerCase?.();
  const mimetype = data.mimetype?.toLowerCase?.();

  if (type) {
    if (type.includes('image')) return FileTypeEnum.Image;
    if (type.includes('video')) return FileTypeEnum.Video;
    if (type.includes('audio')) return FileTypeEnum.Audio;
  }

  if (mimetype) {
    if (mimetype.startsWith('image/')) return FileTypeEnum.Image;
    if (mimetype.startsWith('audio/')) return FileTypeEnum.Audio;
    if (mimetype.startsWith('video/')) return FileTypeEnum.Video;
  }

  return FileTypeEnum.Document;
};

export const downloadFile = async (url: string, filename?: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(`Download failed: ${response.statusText}`);

    const blob = await response.blob();
    const fileName =
      filename || url.split('/').pop() || `download-${Date.now()}`;

    const link = document.createElement('a');
    const blobUrl = window.URL.createObjectURL(blob);

    link.href = blobUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download error:', error);
  }
};

export const allowedExtensions = ['.apk', '.ipk', '.wgt'];

export const getMimeType = (fileName: string): string => {
  const ext = '.' + (fileName.split('.').pop()?.toLowerCase() || '');

  switch (ext) {
    // Apps / packages
    case '.apk':
      return 'application/vnd.android.package-archive';
    case '.ipk':
      return 'application/vnd.webos.ipk';
    case '.wgt':
      return 'application/widget';

    // Images
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.png':
      return 'image/png';
    case '.gif':
      return 'image/gif';
    case '.svg':
      return 'image/svg+xml';
    case '.webp':
      return 'image/webp';

    // Video
    case '.mp4':
      return 'video/mp4';
    case '.mkv':
      return 'video/x-matroska';
    case '.mov':
      return 'video/quicktime';
    case '.avi':
      return 'video/x-msvideo';

    // Audio
    case '.mp3':
      return 'audio/mpeg';
    case '.wav':
      return 'audio/wav';
    case '.ogg':
      return 'audio/ogg';

    // HLS playlists
    case '.m3u8':
      return 'application/vnd.apple.mpegurl';
    case '.m3u':
      return 'application/x-mpegurl';

    // Documents
    case '.pdf':
      return 'application/pdf';
    case '.doc':
      return 'application/msword';
    case '.docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case '.xls':
      return 'application/vnd.ms-excel';
    case '.xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case '.ppt':
      return 'application/vnd.ms-powerpoint';
    case '.pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case '.txt':
      return 'text/plain';
    case '.html':
      return 'text/html';
    case '.json':
      return 'application/json';
    case '.xml':
      return 'application/xml';
    case '.csv':
      return 'text/csv';

    default:
      return 'application/octet-stream';
  }
};

export function getFileCategory(
  mime: string
): 'app' | 'image' | 'video' | 'audio' | 'document' | 'other' {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';

  if (
    [
      'application/vnd.android.package-archive',
      'application/vnd.webos.ipk',
      'application/widget'
    ].includes(mime)
  )
    return 'app';

  if (
    mime.startsWith('text/') ||
    [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/json',
      'application/xml'
    ].includes(mime)
  )
    return 'document';

  return 'other';
}
